import setup from "../wp/_setup.json";
import RecipeDataService from "./RecipeDataService";

class RecipeSchemaService {
  // --- Helper to convert time to ISO8601 ---
  toDuration = (d = 0, h = 0, m = 0) => {
    if (!d && !h && !m) return undefined;
    return `PT${
      d !== null
        ? (d.slice(0, -5) !== "" ? d.slice(0, -5) : d.slice(0, -4)) + "D"
        : ""
    }${
      h !== null
        ? (h.slice(0, -6) !== "" ? h.slice(0, -6) : h.slice(0, -5)) + "H"
        : ""
    }${
      m !== null
        ? (m.slice(0, -8) !== "" ? m.slice(0, -8) : m.slice(0, -7)) + "M"
        : ""
    }`;
  };

   // helper to get the short 'name' from a step text (text until first '.' or ':')
  _extractStepName = (text) => {
    if (!text || typeof text !== "string") return undefined;
    const dot = text.indexOf(".");
    const colon = text.indexOf(":");
    let pos = -1;
    if (dot === -1 && colon === -1) {
      pos = -1;
    } else if (dot === -1) {
      pos = colon;
    } else if (colon === -1) {
      pos = dot;
    } else {
      pos = Math.min(dot, colon);
    }
    if (pos > 0) {
      const name = text.slice(0, pos).trim();
      // fallback to whole text if too short
      return name.length >= 2 ? name : text.trim();
    }
    return text.trim();
  };

  // buildRecipeSchema now accepts optional `comments` array to include reviews
  buildRecipeSchema = (post, data, recipeId, comments = [], tags = [], videoData) => {
    const prepTimeISO = this.toDuration(
      data.prepTimeDays,
      data.prepTimeHours,
      data.prepTimeMinutes
    );

    const cookTimeISO = this.toDuration(
      data.cookTimeDays,
      data.cookTimeHours,
      data.cookTimeMinutes
    );

    const customTimeISO = this.toDuration(
      data.customTimeDays,
      data.customTimeHours,
      data.customTimeMinutes
    );

    const totalTimeISO = this.toDuration(
      data.totalTimeDays,
      data.totalTimeHours,
      data.totalTimeMinutes
    );

    // prefer explicit `tags` passed from the WP post; fall back to plugin `data.keywords`
    const keywords = Array.isArray(tags) && tags.length
      ? tags.join(", ")
      : data.keywords
      ? Array.isArray(data.keywords)
        ? data.keywords.join(", ")
        : data.keywords
      : undefined;

    // Build base schema
    const schema = {
      "@context": "https://schema.org/",
      "@type": "Recipe",
      "@id": `${post.link}#recipe`,
      name: post.title.rendered,
      description: post.excerpt?.rendered?.replace(/(<([^>]+)>)/gi, "") || "",
       // image MUST be an array for rich results
       image: (post.yoast_head_json?.og_image && Array.isArray(post.yoast_head_json.og_image))
         ? post.yoast_head_json.og_image.map(img => img?.url).filter(Boolean)
         : (post.yoast_head_json?.og_image?.[0]?.url ? [post.yoast_head_json.og_image[0].url] : undefined),
      author: {
        "@type": "Person",
        name: "andrews_cookin",
      },
      datePublished: new Date(post.date).toLocaleDateString(
        setup.format.schemaDate.locale,
        setup.format.schemaDate.options
      ),
      dateModified: new Date(post.modified).toLocaleDateString(
        setup.format.schemaDate.locale,
        setup.format.schemaDate.options
      ),
      video: {
        "@type": "VideoObject",
        contentUrl: "https://www.youtube.com/watch?v=" + videoData,
        embedUrl: "https://www.youtube.com/embed/" + videoData + "?feature=oembed",
      },
      ...(prepTimeISO && { prepTime: prepTimeISO }),
      ...(cookTimeISO && { cookTime: cookTimeISO }),
      ...(customTimeISO && { performTime: customTimeISO }),
      ...(totalTimeISO && { totalTime: totalTimeISO }),
      ...(data.servings && { recipeYield: `${data.servings} servings` }),
      ...(data.course && {
        recipeCategory: Array.isArray(data.course)
          ? data.course.join(", ")
          : data.course,
      }),
      ...(data.cuisine && {
        recipeCuisine: Array.isArray(data.cuisine)
          ? data.cuisine.join(", ")
          : data.cuisine,
      }),
  ...(keywords && { keywords }),
      recipeIngredient: data.ingredients || [],
      recipeInstructions:
        (data.instructions &&
          data.instructions.map((stepObj) => {
            // stepObj can be either a string (legacy) or object with text/id
            const text = typeof stepObj === "string" ? stepObj : stepObj.text;
            const name = this._extractStepName(text || "");
            const stepSchema = {
              "@type": "HowToStep",
              name: name || undefined,
              text: text.slice(name.length+1, text.length).trim() || text,
            };
            if (typeof stepObj !== "string" && stepObj.id) {
              const anchor = `${post.link}#${stepObj.id}`;
              stepSchema["@id"] = anchor;
              stepSchema["url"] = anchor;
            }
            return stepSchema;
          })) ||
        [],
      ...(data.equipment?.length
        ? {
            tool: data.equipment.map((item) => ({
              "@type": "HowToTool",
              name: item,
            })),
          }
        : {}),
      ...(data.nutrition && {
        nutrition: {
          "@type": "NutritionInformation",
          calories: data.nutrition + " calories",
        },
      }),
    };

    // If recipe-level rating info was extracted from HTML, add aggregateRating base
    const avg = data.wprm_recipe_rating_average;
    const cnt = data.wprm_recipe_rating_count;

    let ratingValue = null;
    let ratingCount = null;

    if (avg !== null && avg !== undefined) {
      ratingValue = Number(avg);
      if (Number.isNaN(ratingValue)) ratingValue = null;
    }
    if (cnt !== null && cnt !== undefined) {
      ratingCount = Number(cnt);
      if (Number.isNaN(ratingCount)) ratingCount = null;
    }

    // Calculate review list from comments (if provided)
    const reviews = [];
    if (Array.isArray(comments) && comments.length) {
      comments.forEach((c) => {
        // prefer meta or top-level wprm_comment_rating
        const rating =
          c.wprm_comment_rating ??
          (c.meta && (c.meta.wprm_comment_rating || c.meta["wprm_comment_rating"]))
            ? Number(c.meta?.wprm_comment_rating ?? c.meta?.["wprm_comment_rating"] ?? c.wprm_comment_rating)
            : undefined;

        // Only include comments that have a rating or visible review content
        const commentText = (c.content && c.content.rendered) ? this._stripWprmImgFromHtml(c.content.rendered) : "";
        const authorName = c.author_name || (c.author && c.author.name) || null;
        const datePublished = c.date || null;

        // If the comment has rating or review body, include it
        if ((rating !== undefined && !Number.isNaN(Number(rating))) || (commentText && commentText.trim())) {
          const review = {
            "@type": "Review",
            reviewBody: commentText.trim() || undefined,
            datePublished: datePublished || undefined,
            author: authorName ? { "@type": "Person", name: authorName } : undefined,
          };

          if (rating !== undefined && !Number.isNaN(Number(rating))) {
            review.reviewRating = {
              "@type": "Rating",
              ratingValue: Number(rating),
            };
          }

          reviews.push(review);
        }
      });
    }

    // If we have recipe-level rating info OR reviews gathered from comments, create aggregateRating
    const reviewCount = reviews.length > 0 ? reviews.length : null;
    // Only include AggregateRating when there is a numeric rating value or a rating count > 0.
    // Do not create an AggregateRating merely because there are review objects without ratings.
    if (
      (ratingValue !== null && !Number.isNaN(ratingValue) && ratingValue > 0) ||
      (ratingCount !== null && !Number.isNaN(ratingCount) && ratingCount > 0)
    ) {
      const agg = {
        "@type": "AggregateRating",
      };
      if (ratingValue !== null && !Number.isNaN(ratingValue) && ratingValue > 0) agg.ratingValue = ratingValue;
      if (ratingCount !== null && !Number.isNaN(ratingCount) && ratingCount > 0) agg.ratingCount = ratingCount;
      // include reviewCount when available
      if (reviewCount) {
        agg.reviewCount = reviewCount;
      }
      schema.aggregateRating = agg;
    }

    if (reviews.length) {
      schema.review = reviews;
    }

    return schema;
  };

  // Utility to remove the plugin's inline rating <img> from comment HTML and return text only
  _stripWprmImgFromHtml = (html) => {
    if (!html) return "";
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      // remove rating image(s)
      doc.querySelectorAll("img.wprm-comment-rating").forEach((el) => {
        const next = el.nextSibling;
        el.remove();
        if (next && next.nodeType === Node.ELEMENT_NODE && next.tagName.toLowerCase() === "br") {
          next.remove();
        }
      });
      return doc.body.textContent || "";
    } catch (e) {
      return html.replace(/<img[^>]*class=(?:'|")?wprm-comment-rating(?:'|")?[^>]*>\s*(?:<br\s*\/?>)?/gi, "").replace(/<\/?[^>]+(>|$)/g, "");
    }
  };

  extractStepId = (html, id) => {
    const re = new RegExp(`wprm-recipe-${id}-step="(\\d+)"`);
    const match = html.match(re);
    return match ? match[1] : null;
  };

  // extractWprmSchemaFromContent accepts optional comments array
  extractWprmSchemaFromContent(input, posts, comments = [], tags = []) {
    const recipeId = RecipeDataService.extractRecipeId(input);
    const data = RecipeDataService.extractRecipeData(input);
    const videoData = RecipeDataService.extractVideoLink(input);
    // Normalize instructions for buildRecipeSchema:
    // currently buildRecipeSchema expects data.instructions as either strings or objects {text,id}
    const schema = this.buildRecipeSchema(posts, data, recipeId, comments, tags, videoData[1]);

    return schema;
  }
}

export default new RecipeSchemaService();